import { For, Show } from "solid-js";
import { useDenominationsGridV2Context } from "../context/denominations_grid_v2_context";

function MinusIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M4.16675 10H15.8334"
        stroke="currentColor"
        stroke-width="1.67"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M10 4.16675V15.8334M4.16675 10H15.8334"
        stroke="currentColor"
        stroke-width="1.67"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
}

type DenominationRowProps = {
  denomination: number;
  count: number;
  onIncrement: () => void;
  onDecrement: () => void;
  onAdd: () => void;
};

function DenominationRow(props: DenominationRowProps) {
  const isSelected = () => props.count > 0;

  return (
    <div
      class="relative flex h-12 w-full items-center justify-between rounded-lg border border-stroke-2 py-2 pl-4 pr-2"
      classList={{
        "bg-background-mapping-elevation-2": isSelected(),
        "bg-background-normal-primary": !isSelected(),
      }}
    >
      {/* Selected border overlay */}
      <Show when={isSelected()}>
        <div class="pointer-events-none absolute inset-[-1px] rounded-lg border-2 border-stroke-solid" />
      </Show>

      {/* Denomination amount */}
      <span class="text-para-3-semi-bold text-text-normal-primary">
        ₹{props.denomination.toLocaleString()}
      </span>

      {/* Right side: Add button or +/- controls */}
      <Show
        when={isSelected()}
        fallback={
          <button
            class="shadow-sm flex h-8 items-center gap-1 rounded-full border border-stroke-3 bg-background-normal-primary px-3  hover:bg-background-normal-secondary"
            onClick={props.onAdd}
          >
            <span class="text-text-normal-primary">
              <PlusIcon />
            </span>
            <span class="text-title-6-semi-bold text-text-normal-primary">
              Add
            </span>
          </button>
        }
      >
        <div class="flex items-center gap-1">
          {/* Minus button */}
          <button
            class="flex size-8 items-center justify-center rounded-full bg-background-inverted-primary text-text-inverted-primary  hover:opacity-80"
            onClick={props.onDecrement}
          >
            <MinusIcon />
          </button>

          {/* Count display */}
          <span class="w-8 text-center text-title-5-semi-bold text-text-normal-primary">
            {props.count}
          </span>

          {/* Plus button */}
          <button
            class="flex size-8 items-center justify-center rounded-full bg-background-inverted-primary text-text-inverted-primary  hover:opacity-80"
            onClick={props.onIncrement}
          >
            <PlusIcon />
          </button>
        </div>
      </Show>
    </div>
  );
}

export function DenominationsSelector() {
  const gridState = useDenominationsGridV2Context();
  const { denominationList, internalState } = gridState;
  const { denominationCounts, updateDenominationCount } = internalState;

  return (
    <div class="flex w-full flex-col gap-2">
      <For each={denominationList}>
        {(denomination) => (
          <DenominationRow
            denomination={denomination}
            count={denominationCounts[denomination] ?? 0}
            onAdd={() => updateDenominationCount(denomination, 1)}
            onIncrement={() =>
              updateDenominationCount(
                denomination,
                (denominationCounts[denomination] ?? 0) + 1
              )
            }
            onDecrement={() =>
              updateDenominationCount(
                denomination,
                Math.max(0, (denominationCounts[denomination] ?? 0) - 1)
              )
            }
          />
        )}
      </For>
    </div>
  );
}
